import Odometer from "react-odometerjs"
import "odometer/themes/odometer-theme-default.css"
import { makeStyles } from "tss-react/mui"
import { dinero } from "dinero.js"
import { Stack, Typography, Box } from "@mui/material"
import { toCurrency, toDinero, toDineroOrUndefined } from "../../../../currency/currencyLib"
import { ConferenceMerchantQuery, fetchTodaysPosSalesWithEnum } from "../conferenceApi"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { Period } from "../../../../dashboards/dashboardTypes"
import {
    fetchCheckInSummary,
    fetchPosAverageAmount,
    fetchSurveyRatingSummaries,
    MerchantHomeQuery,
} from "../../../../merchant/merchant/home/merchantHomeApi"
import { useMoney } from "../../../../currency/useMoney"
import { displaySmileyWithoutText } from "./helpers"

const useStyles = makeStyles()((theme) => ({
    status: {
        gap: "3rem",
        padding: "0.625rem 3rem 0.25rem",
        textAlign: "center",
        alignItems: "start",
    },
    statusTitle: {
        lineHeight: 1,
        color: theme.palette.tailwind.slate[700],
    },
    statusSubTitle: {
        lineHeight: 1,
        color: theme.palette.tailwind.slate[500],
        paddingLeft: "0.25rem",
    },
    statusInfo: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[700],
        fontSize: "1.875rem",
        lineHeight: 1,
        textAlign: "right",
    },
    statusInfoUpdate: {
        lineHeight: "2.375rem",
    },

    smileyWrap: {
        position: "absolute",
        top: "0",
        left: "50%",
        transform: "translateX(-50%)",
        width: "3.25rem",
        height: "3.25rem",
    },
    smileyImage: {
        objectFit: "contain",
        height: "100%",
    },
}))

export function StatusSection() {
    const { classes, cx } = useStyles()
    const { merchantId } = useMerchant()
    const moneyFactory = useMoney({})

    const { data: todaysPosSalesData } = useApiQuery(fetchTodaysPosSalesWithEnum, {
        queryName: ConferenceMerchantQuery.TODAYS_POS_SALES,
        dependencies: {
            merchantId,
            period: Period.TODAY,
        },
        refetchInterval: 30000,
    })

    const turnoverTotalAmount =
        todaysPosSalesData?.posSales.length &&
        moneyFactory.formatAsNumberWithScale(
            toDinero(todaysPosSalesData.posSales[0]?.salesSummary?.totalAmount),
            "value",
        )
    const turnoverValue = turnoverTotalAmount ? parseFloat(turnoverTotalAmount.replace(",", ".")) : 0

    const { data: posAverageAmountData } = useApiQuery(fetchPosAverageAmount, {
        queryName: MerchantHomeQuery.POS_AVERAGE_AMOUNT,
        dependencies: {
            merchantId: merchantId,
            period: Period.TODAY,
        },
        refetchInterval: 30000,
    })

    const currency = toCurrency("DKK")
    const zero = dinero({ amount: 0, scale: 2, currency })
    const averageTotalAmount = posAverageAmountData?.posSales?.length
        ? toDineroOrUndefined(posAverageAmountData.posSales[0].salesSummary.totalAmount) ?? zero
        : zero

    const averageValue = averageTotalAmount
        ? parseFloat(moneyFactory.formatAsNumber(averageTotalAmount).replace(",", "."))
        : 0

    const { data: checkInSummaryData } = useApiQuery(fetchCheckInSummary, {
        queryName: MerchantHomeQuery.CHECK_IN_SUMMARY,
        dependencies: {
            merchantId,
            period: Period.LAST_MONTH,
        },
        refetchInterval: 30000,
    })

    const { data: surveyRatingSummariesData } = useApiQuery(fetchSurveyRatingSummaries, {
        queryName: MerchantHomeQuery.SURVEY_RATING_SUMMARIES,
        dependencies: {
            merchantId,
            periodInDays: 30,
        },
        refetchInterval: 30000,
    })

    return (
        <Stack direction="row" className={classes.status}>
            <Stack spacing={1}>
                <LocalizedStrict id="merchant-home-views-dialog-conference-orders">
                    <Typography variant="body1" className={classes.statusTitle}>
                        Orders
                    </Typography>
                </LocalizedStrict>
                <Odometer
                    value={
                        (todaysPosSalesData?.posSales &&
                            todaysPosSalesData?.posSales[0]?.salesSummary.numberOfOrders) ||
                        0
                    }
                    className={classes.statusInfo}
                />
            </Stack>
            <Stack spacing={1}>
                <Typography variant="body1" className={classes.statusTitle}>
                    <LocalizedStrict id="merchant-home-views-dialog-conference-turnover">
                        <>Turnover</>
                    </LocalizedStrict>
                    <Typography variant="caption" className={classes.statusSubTitle}>
                        {(todaysPosSalesData?.posSales.length &&
                            todaysPosSalesData.posSales[0]?.salesSummary?.totalAmount.currency) ||
                            "DKK"}
                    </Typography>
                </Typography>

                <Odometer value={turnoverValue} className={classes.statusInfo} />
            </Stack>
            <Stack spacing={1}>
                <Typography variant="body1" className={classes.statusTitle}>
                    <LocalizedStrict id="merchant-home-views-dialog-conference-avg-price">
                        <>Avg price</>
                    </LocalizedStrict>
                    <Typography variant="caption" className={classes.statusSubTitle}>
                        DKK
                    </Typography>
                </Typography>

                <Odometer value={averageValue} className={classes.statusInfo} />
            </Stack>
            <Stack spacing={1}>
                <LocalizedStrict id="merchant-home-views-dialog-conference-free-riders">
                    <Typography variant="body1" className={classes.statusTitle}>
                        Free riders
                    </Typography>
                </LocalizedStrict>
                <Stack flexDirection="row" justifyContent="end">
                    <Odometer
                        value={checkInSummaryData?.checkInSummary?.checkInsPercentage || 0}
                        className={classes.statusInfo}
                        format="d"
                    />
                    <Typography className={cx(classes.statusInfo, classes.statusInfoUpdate)}>%</Typography>
                </Stack>
            </Stack>
            <Stack spacing={1}>
                <LocalizedStrict id="merchant-home-views-dialog-conference-satisfaction">
                    <Typography variant="body1" className={classes.statusTitle}>
                        Satisfaction
                    </Typography>
                </LocalizedStrict>
                <Box position="relative">
                    {surveyRatingSummariesData?.surveys && !!surveyRatingSummariesData?.surveys.length && (
                        <Box className={classes.smileyWrap}>
                            <img
                                src={displaySmileyWithoutText(
                                    surveyRatingSummariesData.surveys[0].ratingsSummary.value,
                                )}
                                className={classes.smileyImage}
                            />
                        </Box>
                    )}
                </Box>
            </Stack>
        </Stack>
    )
}
